import { motion } from 'framer-motion';
import { MapPin, Calendar, Navigation, ChevronRight } from 'lucide-react';
import { openDirections } from '../Events/EventDetailModal';

const StatusBadge = ({ status }) => {
  const config = {
    active: { label: 'EN COURS', className: 'bg-emerald-500 text-white' },
    upcoming: { label: 'À VENIR', className: 'bg-[#97c7c3] text-black' },
    past: { label: 'TERMINÉ', className: 'bg-gray-300 text-gray-600' }
  };
  const { label, className } = config[status] || config.upcoming;
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${className}`}>
      {status === 'active' && <span className="w-1.5 h-1.5 bg-white rounded-full mr-1 animate-pulse" />}
      {label}
    </span>
  );
};

export const ListView = ({ events, currentEvent, onEventSelect, allEvents }) => {
  // Group events by month
  const groupedEvents = events.reduce((acc, event) => {
    const date = new Date(event.dates.start);
    const monthKey = date.toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' });
    if (!acc[monthKey]) acc[monthKey] = [];
    acc[monthKey].push(event);
    return acc;
  }, {});

  return (
    <motion.div
      data-testid="list-view"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-10 bg-white overflow-y-auto"
    >
      <div className="max-w-2xl mx-auto px-4 pt-24 md:pt-6 pb-[200px]">
        <h1 className="font-league text-3xl uppercase tracking-wide text-black mb-6">
          Tous les événements
        </h1>

        {Object.entries(groupedEvents).map(([month, monthEvents]) => (
          <div key={month} className="mb-8">
            <h2 className="font-league text-lg uppercase tracking-wide text-[#baa25e] mb-4 sticky top-0 bg-white py-2 z-10">
              {month}
            </h2>
            
            <div className="space-y-3">
              {monthEvents.map((event, index) => {
                const isPast = event.status === 'past';
                
                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-white rounded-2xl border-2 overflow-hidden transition-all duration-200 ${
                      currentEvent?.id === event.id 
                        ? 'border-[#97c7c3] shadow-lg' 
                        : isPast 
                          ? 'border-gray-100 opacity-60' 
                          : 'border-gray-100 hover:border-[#97c7c3] hover:shadow-md'
                    }`}
                    data-testid={`list-event-${event.id}`}
                  >
                    <button
                      onClick={() => {
                        const eventIndex = allEvents.findIndex(e => e.id === event.id);
                        if (eventIndex >= 0) onEventSelect(eventIndex);
                      }}
                      className="w-full text-left p-4"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <StatusBadge status={event.status} />
                            <span className="font-dm text-xs text-gray-400">{event.type}</span>
                          </div>
                          
                          <h3 className={`font-league text-lg uppercase tracking-wide mb-2 ${
                            isPast ? 'text-gray-400' : 'text-black'
                          }`}>
                            {event.name}
                          </h3>
                          
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                            <div className="flex items-center gap-1.5">
                              <MapPin className={`w-3.5 h-3.5 ${isPast ? 'text-gray-300' : 'text-[#baa25e]'}`} />
                              <span className={`font-dm text-sm ${isPast ? 'text-gray-400' : 'text-gray-600'}`}>
                                {event.location}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className={`w-3.5 h-3.5 ${isPast ? 'text-gray-300' : 'text-[#baa25e]'}`} />
                              <span className={`font-dm text-sm ${isPast ? 'text-gray-400' : 'text-gray-600'}`}>
                                {event.displayDates}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <ChevronRight className={`w-5 h-5 flex-shrink-0 ${isPast ? 'text-gray-300' : 'text-gray-400'}`} />
                      </div>
                    </button>
                    
                    {/* Quick action - Directions */}
                    {!isPast && (
                      <div className="border-t border-gray-100 px-4 py-2 flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDirections(event);
                          }}
                          className="flex items-center gap-1.5 text-[#97c7c3] font-dm text-sm font-medium hover:text-[#7dbbb6] transition-colors"
                          data-testid={`directions-${event.id}`}
                        >
                          <Navigation className="w-3.5 h-3.5" />
                          Itinéraire
                        </button>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}

        {events.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <p className="font-league text-xl uppercase tracking-wide text-gray-400 mb-2">
              Aucun événement
            </p>
            <p className="font-dm text-sm text-gray-400">
              Essayez de modifier vos filtres
            </p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};
